import {
  NotificationPayload,
  NotificationPayloadMap,
  NotificationType,
} from "@/types";

// Shared candidacy deep-link format — used both for whole-notification links
// (below) and per-item links (e.g. each candidacy row in an expiring-candidates list).
export function getCandidacyLink({
  candidacyId,
  companyId,
  jobId,
}: {
  candidacyId: string;
  companyId: string;
  jobId: string;
}): string {
  // Pipeline is pinned explicitly — the candidate sheet only mounts on that tab.
  return `/companies/${companyId}/jobs/${jobId}?t=pipeline&c=${candidacyId}`;
}

// Job deep-link — used for per-item links (e.g. each job row in the
// paused-jobs reminder list).
export function getJobLink({
  companyId,
  jobId,
}: {
  companyId: string;
  jobId: string;
}): string {
  return `/companies/${companyId}/jobs/${jobId}`;
}

/**
 * Builds the deep link for a notification. One case per NotificationType,
 * since each ships its own payload shape (see NotificationPayloadMap).
 */
export function getNotificationLink(
  type: NotificationType,
  payload: NotificationPayload,
): string | null {
  switch (type) {
    case NotificationType.EXCELLENT_CANDIDATE:
    case NotificationType.INTERNAL_RECOMMENDATION:
    case NotificationType.INTERNAL_COMMENT_MENTION:
    case NotificationType.THREAD_REPLY:
    case NotificationType.CANDIDATE_RESURRECTED_AFTER_STEVIE:
    case NotificationType.ACTIVE_CANDIDATE_FINISHED_STEVIE:
    case NotificationType.CANDIDACY_AUTO_SCREENED: {
      const { candidacyId, companyId, jobId } =
        payload as NotificationPayloadMap[
          | NotificationType.EXCELLENT_CANDIDATE
          | NotificationType.INTERNAL_RECOMMENDATION
          | NotificationType.INTERNAL_COMMENT_MENTION
          | NotificationType.THREAD_REPLY
          | NotificationType.ACTIVE_CANDIDATE_FINISHED_STEVIE
          | NotificationType.CANDIDATE_RESURRECTED_AFTER_STEVIE
          | NotificationType.CANDIDACY_AUTO_SCREENED];
      return getCandidacyLink({ candidacyId, companyId, jobId });
    }
    case NotificationType.JOB_COMMENT_MENTION: {
      const { companyId, jobId } =
        payload as NotificationPayloadMap[NotificationType.JOB_COMMENT_MENTION];
      return `/companies/${companyId}/jobs/${jobId}?t=comments`;
    }
    case NotificationType.COMPANY_COMMENT_MENTION: {
      const { companyId } =
        payload as NotificationPayloadMap[NotificationType.COMPANY_COMMENT_MENTION];
      return `/companies/${companyId}?t=comments`;
    }
    case NotificationType.TEAM_WIDE_SUBMIT: {
      const { companyId, jobId } =
        payload as NotificationPayloadMap[NotificationType.TEAM_WIDE_SUBMIT];
      return getJobLink({ companyId, jobId });
    }
    case NotificationType.JOB_PAUSE_REQUEST_DECIDED: {
      const { companyId, jobId } =
        payload as NotificationPayloadMap[NotificationType.JOB_PAUSE_REQUEST_DECIDED];
      return getJobLink({ companyId, jobId });
    }
    case NotificationType.PLACEMENT_CHECKINS:
    case NotificationType.MISSING_START_DATES:
    case NotificationType.COMMS_TO_SEND:
      return "/?panel=tasks";
    case NotificationType.LEADERBOARD_DAILY:
    case NotificationType.LEADERBOARD_WEEKLY:
      return "/leaderboard";
    default:
      return null;
  }
}
